import * as React from 'react';

import { Badge, Button, Row, Tab } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideArchive, LucidePencil, LucideRotateCcw } from 'lucide-react';

import { RoleActionConfirmationDialog } from '../_shared/RoleActionConfirmationDialog';

import { useRoleQuery } from './_graphql/useRoleQuery';
import { useRoleUsersQuery } from './_graphql/useRoleUsersQuery';
import { useRoleActionDialog } from './_hooks/useRoleActionDialog';
import { AddUsersToRoleDialog } from './AddUsersToRoleDialog';
import { RenameRoleDialog } from './RenameRoleDialog';
import { RolePermissionsTab } from './RolePermissionsTab';
import { RoleUsersTab } from './RoleUsersTab';

import { SettingsSubmodule } from '~/modules/settings/_components/_layouts/SettingsSubmodule';
import { formatMonthYear } from '~/modules/settings/_components/_shared/utils';
import {
  $currentRole,
  $initRolePermissions,
  $roleDetailsTab,
  $roleId,
  type RoleDetailsTab,
} from '~/modules/settings/_state/roleForm.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { PermissionGate } from '~/shared/components/PermissionGate';

export const RoleDetailsView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const roleId = useAtomValue($roleId);
  const [currentTab, setCurrentTab] = useAtom($roleDetailsTab);
  const currentRole = useAtomValue($currentRole);
  const setCurrentRole = useSetAtom($currentRole);
  const initPermissions = useSetAtom($initRolePermissions);

  const { data } = useRoleQuery({ roleId: roleId ?? '' });

  const { data: usersData } = useRoleUsersQuery({
    roleId: roleId ?? '',
    limit: 1000,
    offset: 0,
  });

  const userCount = usersData?.total ?? 0;

  React.useEffect(() => {
    if (data?.role) {
      setCurrentRole(data.role);
    }
  }, [data, setCurrentRole]);

  React.useEffect(() => {
    if (currentRole) {
      initPermissions();
    }
  }, [currentRole, initPermissions]);

  const [isRenameDialogOpen, setIsRenameDialogOpen] = React.useState(false);

  const roleActionDialog = useRoleActionDialog({
    onSuccess: (actionType) => {
      if (actionType === 'archive') {
        setLocation('/team-and-permissions/roles');
      }
    },
  });

  const handleBack = () => {
    setLocation('/team-and-permissions/roles');
  };

  const handleArchiveClick = () => {
    if (!currentRole) return;
    roleActionDialog.openForRole(currentRole);
  };

  const handleTabChange = (tab: RoleDetailsTab) => {
    setCurrentTab(tab);
  };

  const isArchived = currentRole?.status === 'archived';

  const subtitle = currentRole
    ? `Last updated on ${formatMonthYear(currentRole.updatedAt)}${currentRole.updatedBy ? ` by ${currentRole.updatedBy}` : ''}`
    : 'Loading...';

  return (
    <>
      <SettingsSubmodule
        className="@RoleDetailsView h-full"
        contentClassName="p-0"
        title={currentRole?.name ?? 'Loading...'}
        subtitle={subtitle}
        onBackPressed={handleBack}
        tools={
          currentRole && (
            <PermissionGate action="roleUpdate">
              <Button
                variant="outline"
                icon={<LucidePencil className="size-4" />}
                onPress={() => setIsRenameDialogOpen(true)}
              >
                {t.teamAndPermissions.roles.details.actions.rename}
              </Button>
              <Button
                variant="outline"
                icon={
                  isArchived ? (
                    <LucideRotateCcw className="size-4" />
                  ) : (
                    <LucideArchive className="size-4" />
                  )
                }
                onPress={handleArchiveClick}
                isDisabled={roleActionDialog.isPending}
              >
                {isArchived
                  ? t.teamAndPermissions.roles.details.actions.restore
                  : t.teamAndPermissions.roles.details.actions.archive}
              </Button>
            </PermissionGate>
          )
        }
        tabs={
          <>
            <Tab
              selected={currentTab === 'permissions'}
              onClick={() => handleTabChange('permissions')}
            >
              {t.teamAndPermissions.roles.details.tabs.permissions}
            </Tab>
            <Tab
              selected={currentTab === 'users'}
              onClick={() => handleTabChange('users')}
            >
              <Row className="items-center gap-1.5">
                {t.teamAndPermissions.roles.details.tabs.users}
                {userCount > 0 && (
                  <Badge color="neutral" className="text-xs">
                    {userCount}
                  </Badge>
                )}
              </Row>
            </Tab>
          </>
        }
      >
        {currentTab === 'permissions' && (
          <RolePermissionsTab
            onRestore={handleArchiveClick}
            isRestorePending={roleActionDialog.isPending}
          />
        )}
        {currentTab === 'users' && <RoleUsersTab />}
      </SettingsSubmodule>
      <AddUsersToRoleDialog />
      <RenameRoleDialog
        isOpen={isRenameDialogOpen}
        role={currentRole}
        onOpenChange={setIsRenameDialogOpen}
      />

      <RoleActionConfirmationDialog
        dialogState={roleActionDialog.dialogState}
        dialogs={t.teamAndPermissions.roles.dialogs}
        isPending={roleActionDialog.isPending}
        onConfirm={roleActionDialog.confirm}
        onCancel={roleActionDialog.cancel}
        onOpenChange={roleActionDialog.handleOpenChange}
        className="@RoleDetailsViewRoleActionDialog"
      />
    </>
  );
};
